// Interface strings and formats (WS-49, contract section 6). `web/js/i18n/<lang>.json` is flat `key: string`; a package
// may override or add keys (settings.json `strings`). A key is looked up in the language on screen, then the map's
// default language, then English, then the first language that has it, then it is empty: a key name is never shown.
// Dates and numbers go through Intl in the language on screen.
import { t as pick } from './settings/index.js';

const baseOf = (tag) => String(tag || '').toLowerCase().split('-')[0];

/** A translator over string tables. Pure: no DOM, no network (the loader is below). */
export function createI18n() {
  const shipped = {}, extra = {}, formats = new Map();
  let lang = 'en', fallback = 'en', source = 'en', tags = [];
  const fmt = (Type, opts) => {
    const k = Type.name + JSON.stringify(opts);
    if (!formats.has(k)) formats.set(k, new Type(lang, opts));
    return formats.get(k);
  };
  const day = (m, d, opts) => fmt(Intl.DateTimeFormat, { ...opts, timeZone: 'UTC' }).format(Date.UTC(2000, m - 1, d)); // 2000: a leap year
  const i = {
    get lang() { return lang; },
    /** True when the language on screen is the one the package's plain strings are written in. */
    get isDefault() { return baseOf(lang) === baseOf(source); },
    /**
     * The language on screen, the default language of the page (the fallback of every lookup) and the language the
     * package's plain strings are written in (`src`: the first language of the map; without it, the default). A
     * preset may open in another language than the package was written in (`lang.default: "fi"` on an English
     * package): its plain labels are then still English. An unusable tag falls back to the default, then English.
     */
    use(l, def, src) {
      const ok = (tag) => { try { return typeof tag === 'string' && !!Intl.getCanonicalLocales(tag)[0]; } catch { return false; } }; // errors:ignore: a malformed tag from data is simply not used
      fallback = ok(def) ? def : 'en'; lang = ok(l) ? l : fallback; source = ok(src) ? src : fallback;
      formats.clear();
    },
    /** Adds a table for one language. `pkg`: the package's own strings, which win over the shipped ones. */
    add(l, table, pkg = false) {
      const to = pkg ? extra : shipped, b = baseOf(l);
      to[b] = { ...to[b], ...table };
      tags = [...new Set([...Object.keys(shipped), ...Object.keys(extra)])];
    },
    loaded: (l) => baseOf(l) in shipped,
    /** The text of an interface key, `{name}` placeholders filled from `vars`. */
    T(key, vars) {
      const field = {};
      for (const l of tags) { const s = extra[l]?.[key] ?? shipped[l]?.[key]; if (typeof s === 'string') field[l] = s; }
      const s = pick(field, lang, fallback);
      return vars ? s.replace(/\{(\w+)\}/g, (m, k) => vars[k] ?? '') : s;
    },
    /** The text of a translatable content field (contract section 6). */
    tx: (field) => pick(field, lang, fallback),
    /** A package label that may still be a plain string: in another language than the package's own the
     *  interface's text `alt` is shown instead (season names, date chips, snow levels). */
    named: (label, alt) => (typeof label === 'string' && !i.isDefault ? alt : '') || pick(label, lang, fallback) || alt || '',
    num: (n, digits = 0) => fmt(Intl.NumberFormat, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n),
    list: (items) => fmt(Intl.ListFormat, { type: 'conjunction' }).format(items),
    lower: (s) => String(s).toLocaleLowerCase(lang),
    /** The clock for an hour of the day (0..24, fractions = minutes). */
    clock: (h) => fmt(Intl.DateTimeFormat, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' }).format(Date.UTC(2000, 0, 1, Math.floor(h) % 24, Math.round((h % 1) * 60) % 60)),
    /** The parts of a day of the year for the `{d} {mon}` kind of keys: numbers, month names and the full date. */
    dayVars: (m, d) => ({ d: i.num(d), m: i.num(m), mon: day(m, d, { month: 'short' }), month: day(m, d, { month: 'long' }), date: day(m, d, { day: 'numeric', month: 'long' }) }),
  };
  return i;
}

/** The page's translator. */
export const i18n = createI18n();
export const T = i18n.T;

const pending = {};
/** Fetches `web/js/i18n/<lang>.json` once (a regional tag loads its base language). A missing file adds no strings. */
export function loadStrings(l) {
  const b = baseOf(l);
  if (!/^[a-z]{2,3}$/.test(b)) { i18n.add(b, {}); return Promise.resolve(); } // no such file can exist
  return (pending[b] ||= fetch(new URL(`./i18n/${b}.json`, import.meta.url))
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({})) // errors:ignore: without the file the lookup falls back to the default language and English
    .then((table) => i18n.add(b, table)));
}
